import { registerApplication, start } from 'single-spa';
import * as Vue from 'vue';
import { createInstance, type ModuleFederationRuntimePlugin } from '@module-federation/runtime';
import { CONTAINER_LOADED_EVENT, type ContainerLifecycleDetail } from '@abax/platform';
import { installPopstateCorral } from '@/utils/popstate-corral';
import './styles/ios-100vh-fix';
import build from '@/meta/build';
import config, { loadConfig } from './config';
import { showConfigError } from './config-error';
import { SHELL_APP } from '@/config/app-name';
import { ABAX_UI, ABAX_UI_STYLES_MODULE } from '@/config/federation-package-ids';
import { createAbaxUiSecurityPlugin } from '@/utils/module-federation/mf2-security-plugin';
import { getTrustedAbaxUiManifestPublicKeys } from '@/config/abax-ui-manifest-public-key';
import { setPreloadPromise } from '@/utils/module-federation/preload-registry';
import {
  abandonAbaxUiPreview,
  readAbaxUiPreviewManifestUrl,
} from '@/utils/module-federation/abax-ui-preview';
import { installFederatedPackage } from '@/utils/module-federation';
import { reloadPage } from '@/services/page-lifecycle';

// Legacy classic remote-entry load for abax-ui, used when no signed
// `mf-manifest.json` URL is configured: the committed default, local dev (no
// local signing), and any environment not yet cut over. Loads the plain
// remoteEntry.js from `ABAX_UI_REMOTE_URL` via the classic MF path — the same
// behavior the shell shipped before MF2 adoption. Unsigned by design here: the
// manifest SRI/pin tamper defenses are inactive on this path (documented posture
// during the incremental rollout), and it never activates once the env's
// `ABAX_UI_MANIFEST_URL` is flipped to the signed manifest at cutover.
async function preloadAbaxUiLegacy(): Promise<void> {
  const url = config.ABAX_UI_REMOTE_URL;
  if (!url) return; // nothing configured — skip; the readiness gate handles it
  setPreloadPromise(ABAX_UI, Promise.resolve(url));
  try {
    // installFederatedPackage validates the URL (allowlist) and loads the
    // remoteEntry.js, which self-registers on `window.abax_ui`.
    await installFederatedPackage(ABAX_UI, url);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[ABAX_UI] legacy preload failed', { scope: ABAX_UI, cause: err });
    window.__ABAX_UI_LOAD_FAILED__ = true;
  }
}

async function preloadAbaxUi(): Promise<void> {
  // Read from the typed config module rather than the raw `window['shell/__env__']`
  // bag - keeps env-var renames type-checked at the call site instead of failing
  // silently at runtime. `config` and the window key reference the same object.
  //
  // Under the MF2 runtime the entry is the signed `mf-manifest.json` URL. When it
  // is empty — the committed default, local dev, and any environment not yet cut
  // over — the signed trust root is not wired, so fall back to the LEGACY classic
  // remote-entry load. Production stays on that path until its
  // `ABAX_UI_MANIFEST_URL` is flipped at the coordinated cutover, so merging this
  // does not change a running environment (production untouched).
  if (!config.ABAX_UI_MANIFEST_URL) {
    await preloadAbaxUiLegacy();
    return;
  }
  const previewManifestUrl = readAbaxUiPreviewManifestUrl(config);
  const manifestUrl = previewManifestUrl ?? config.ABAX_UI_MANIFEST_URL;

  // Publish the manifest URL the host loads so `getResolvedUrl` has real data and
  // `detectAbaxUiUrlDrift` can compare against it (rather than silently falling
  // back to the legacy `ABAX_UI_REMOTE_URL`). Under MF2 the resolved entry IS the
  // signed manifest URL; `syncAbaxUiPreview` reads it to tell a preview load apart.
  setPreloadPromise(ABAX_UI, Promise.resolve(manifestUrl));

  // One MF2 runtime instance for the abax-ui remote. The security plugin owns
  // the whole trust chain via its runtime hooks — manifest-URL allowlist,
  // detached-JWS verify (fail-closed), commit pin, remoteEntry SRI, and the
  // tamper-vs-transient fork. The host only wires the out-of-band public key
  // (baked into this bundle, NOT fetched from the CDN) and the degrade signal.
  const instance = createInstance({
    name: 'shell',
    remotes: [{ name: ABAX_UI, entry: manifestUrl }],
    // MF2 keeps its OWN share registry (`__FEDERATION__.__SHARE__`) and never
    // reads the classic webpack `__webpack_share_scopes__` this shell fills. So
    // a manually-created instance shares nothing unless it declares it here.
    // Provide the host's already-loaded eager singleton Vue so `loadRemote`
    // inits the abax-ui container against a scope that holds it — otherwise the
    // remote would resolve a SECOND Vue and break reactivity / provide-inject
    // across every abax-ui component. The classic promise-remote path inits the
    // same container with the same host Vue, so first-init-wins is harmless.
    shared: {
      vue: {
        version: Vue.version,
        lib: () => Vue,
        shareConfig: { singleton: true, requiredVersion: '^3.5.0', eager: true },
      },
    },
    // The plugin is intentionally decoupled from MF2's internal hook-payload
    // types (its hooks are typed against the narrow shapes it reads, not the
    // full `ModuleFederationRuntimePlugin` surface). The security logic — not
    // the types — is the contract, so adapt at this one boundary rather than
    // couple the plugin to MF2 internal type paths.
    plugins: [
      createAbaxUiSecurityPlugin({
        // Environment-scoped trust set (forward-deploy invariant): production
        // trusts only the production key; staging trusts staging + production so
        // every staging deploy (feature branch or master) verifies. Selected from
        // the runtime `ENVIRONMENT` so one prod-built image serves every env.
        publicKeyPems: getTrustedAbaxUiManifestPublicKeys(config.ENVIRONMENT),
        // Empty string from env-config.json disables the pin check (dev /
        // unpinned deploy). CI injects the deployed SHA via
        // K8S_SECRET_ABAX_UI_EXPECTED_COMMIT to activate tamper detection.
        expectedCommit: config.ABAX_UI_EXPECTED_COMMIT || undefined,
        // Runtime fail-closed policy — a single prod-built image can be
        // deployed to environments with different security postures. Flip
        // K8S_SECRET_ABAX_UI_REQUIRE_COMMIT_PIN to make a forgotten pin fail
        // the load instead of warning. Default `false` keeps `pnpm run serve`
        // permissive.
        requireCommitPin: config.ABAX_UI_REQUIRE_COMMIT_PIN === true,
        skipFreshnessFloor: previewManifestUrl !== null,
        // Operator-tunable manifest fetch timeout (env-config). `0`/unset falls
        // back to the plugin's baked-in default; the strict `> 0` guard lives in
        // the plugin so a negative value can't abort the fetch immediately.
        timeoutMs:
          typeof config.ABAX_UI_MANIFEST_TIMEOUT_MS === 'number' &&
          config.ABAX_UI_MANIFEST_TIMEOUT_MS > 0
            ? config.ABAX_UI_MANIFEST_TIMEOUT_MS
            : undefined,
        onDegraded: () => {
          window.__ABAX_UI_LOAD_FAILED__ = true;
        },
      }) as unknown as ModuleFederationRuntimePlugin,
    ],
  });

  try {
    // Trigger the secure load. `loadRemote` drives the plugin's
    // fetch → loadRemoteSnapshot → createScript hooks; on success the webpack
    // container self-registers on `window.abax_ui`, which
    // modules/federation/app-registration.ts and sidecar-execution.ts consume
    // unchanged. The expose key drops the leading `./` (`./styles` → `styles`).
    await instance.loadRemote(`${ABAX_UI}/${ABAX_UI_STYLES_MODULE.replace(/^\.\//, '')}`);
    // The container is installed and verified. Announce it so consumers polling
    // for readiness (ensureRemoteReady) resolve on this event instead of
    // waiting a poll tick. Matches @abax/platform's container lifecycle
    // contract - same event name and Date.now() timestamp the SDK emits.
    window.dispatchEvent(
      new CustomEvent<ContainerLifecycleDetail>(CONTAINER_LOADED_EVENT, {
        detail: { scope: ABAX_UI, timestamp: Date.now() },
      }),
    );
  } catch (err) {
    // The plugin's `errorLoadRemote` fork already flipped
    // `__ABAX_UI_LOAD_FAILED__` (via onDegraded) for tamper/transient failures;
    // set it again defensively so ANY loadRemote rejection — including one that
    // never reached the fork — surfaces to the bootloader's readiness check.
    // Never rethrow: boot must not break on a failed remote. Log first so an
    // operator triaging "abax-ui isn't loading" sees the cause, not just the
    // flag.
    // eslint-disable-next-line no-console
    console.error('[ABAX_UI] preload failed', { scope: ABAX_UI, cause: err });
    window.__ABAX_UI_LOAD_FAILED__ = true;
  }

  // federation-config cannot tell a preview that failed here from one that loaded.
  if (previewManifestUrl !== null && window.__ABAX_UI_LOAD_FAILED__) {
    abandonAbaxUiPreview(previewManifestUrl, () => reloadPage('stale-asset'));
  }
}

// env-config is pre-loaded
loadConfig()
  .then(() => {
    preloadAbaxUi(); // fire-and-forget: starts fetching abax-ui in parallel

    if (build.isUnderMaintenance) {
      import(/* webpackChunkName: "bootloader-maintenance" */ './bootloader-maintenance');
    } else {
      // IMPORTANT! need to load the bootstrapper async, this allows us to
      // share our federated module dependencies in a non-eager way.

      // register shell as a single-spa application
      registerApplication(
        SHELL_APP,
        () => import(/* webpackChunkName: "bootloader" */ './bootloader'),
        () => true, // always active
      );

      start({ urlRerouteOnly: true });

      // MUST be called AFTER start(): single-spa's start() patches
      // addEventListener, the corral must wrap that as the outermost layer.
      // Safe HERE because single-spa 6 schedules the first reroute via
      // promise continuations - start() returns before any app factory runs.
      installPopstateCorral();
    }
  }, showConfigError)
  .catch((err) => console.log(err));

// make typescript look at this file as a module (ref isolatedModules option).
export {};
